/**
 * The words the screens use for values the API sends as keys. One place, so a field, a page or a
 * confidence reads the same on every screen and never as the key it is stored under.
 */
import { FIELD_LABELS } from "./api";

/** A field name as an engineer says it: "Manufacturer", not `make_manufacturer`. */
export function fieldLabel(name: string | null | undefined): string {
  if (!name) return "";
  const known = FIELD_LABELS[name];
  if (known) return known;
  const words = name.replace(/_/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const NAMED: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: "\"", apos: "'" };

/**
 * A folder or file name as a person reads it. The MSO export wrote the colon Windows forbids as
 * `&#x3a;` (`Manufacturing Databook (QA&#x3a;QC)`), so the name on disk carries the reference. The
 * path keeps it, because the path is the file's identity; only what is shown is decoded. A bare
 * `&` ("Fire & Gas") and anything that is not a well-formed reference are left as written.
 */
export function displayName(name: string | null | undefined): string {
  return (name ?? "").replace(/&(?:#[xX]([0-9a-fA-F]{1,6})|#([0-9]{1,7})|(amp|lt|gt|quot|apos));/g,
    (whole, hex: string | undefined, dec: string | undefined, named: string | undefined) => {
      if (named) return NAMED[named];
      const code = hex ? parseInt(hex, 16) : Number(dec);
      return code > 0 && code <= 0x10ffff && !(code >= 0xd800 && code <= 0xdfff) ? String.fromCodePoint(code) : whole;
    });
}

/** The file a path names, whichever separator it uses. The server's folders are never shown. */
export function fileName(path: string | null | undefined): string {
  const name = (path ?? "").split(/[\\/]/).filter(Boolean).pop();
  return name ? displayName(name) : "file not recorded";
}

/** "page 4", or "page unknown" when the reader could not say. */
export function pageLabel(page: number | null | undefined): string {
  return page ? `page ${page}` : "page unknown";
}

/** A 0 to 1 confidence as a whole percent. */
export function percent(value: number | null | undefined): string {
  return typeof value === "number" && Number.isFinite(value) ? `${Math.round(value * 100)}%` : "";
}

/** A key such as `no_notice_found` in plain words, for values no table below names. */
export function words(key: string | null | undefined): string {
  return String(key ?? "").replace(/_/g, " ").trim();
}

const LIFECYCLE: Record<string, string> = {
  obsolete: "obsolete",
  announced: "end of life announced",
  active: "active",
  no_notice_found: "no published notice found",
};

/** A lifecycle finding. `no_notice_found` is never shortened to anything that reads as active. */
export function lifecycleLabel(status: string | null | undefined): string {
  return status ? LIFECYCLE[status] ?? words(status) : "";
}

const UC5_STAGES: Record<string, string> = {
  spec_sourcing: "Specification",
  obsolescence: "Obsolescence check",
  market: "Market search",
  ltb: "Last-time buy",
};

export function vendorStageLabel(stage: string | null | undefined): string {
  return stage ? UC5_STAGES[stage] ?? words(stage) : "";
}
